import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Custom element for the Ax Collection Heading section.
 *
 * La description est rendue FORMATÉE et repliée visuellement par CSS
 * (`-webkit-line-clamp`, N lignes réglables). Ce composant :
 *   1. ne révèle le bouton « En savoir plus » que si le texte déborde
 *      réellement des N lignes (mesuré, réévalué au resize) — une description
 *      courte n'est jamais repliée, quel que soit son nombre de mots ;
 *   2. gère le toggle replié/déplié (libellés + aria-expanded).
 *
 * @extends {Component}
 */
class AxCollectionHeading extends Component {
	/** @type {ResizeObserver | null} */
	#resizeObserver = null;

	connectedCallback() {
		super.connectedCallback();

		const description = this.querySelector('.ax-collection-heading__description');
		if (!(description instanceof HTMLElement) || !this.#button) return;

		this.#syncButtonVisibility();
		this.#resizeObserver = new ResizeObserver(() => this.#syncButtonVisibility());
		this.#resizeObserver.observe(description);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#resizeObserver?.disconnect();
	}

	get #button() {
		const button = this.querySelector('.ax-collection-heading__readmore');
		return button instanceof HTMLElement ? button : null;
	}

	/**
	 * Le bouton n'a de raison d'être que si le clamp tronque réellement le texte.
	 * Un texte déplié par l'utilisateur garde son bouton (pour re-replier).
	 */
	#syncButtonVisibility() {
		const description = this.querySelector('.ax-collection-heading__description');
		const button = this.#button;
		if (!(description instanceof HTMLElement) || !button) return;

		if (description.dataset.collapsed !== 'true') return;
		button.hidden = description.scrollHeight <= description.clientHeight + 1;
	}

	/**
	 * Toggles the collapsed/expanded state of the description.
	 *
	 * @param {Event} event - The click event (delegated; target is the <button>).
	 */
	toggleDescription(event) {
		const target = event.target;
		const button = target instanceof HTMLElement ? target.closest('button') : null;
		if (!button) return;

		const description = this.querySelector('.ax-collection-heading__description');
		if (!(description instanceof HTMLElement)) return;

		const collapsed = description.dataset.collapsed === 'true';

		this.#animateHeight(description, collapsed);

		description.dataset.collapsed = collapsed ? 'false' : 'true';
		button.setAttribute('aria-expanded', collapsed ? 'true' : 'false');

		const more = button.querySelector('.ax-collection-heading__readmore-more');
		const less = button.querySelector('.ax-collection-heading__readmore-less');
		if (more instanceof HTMLElement) more.hidden = collapsed;
		if (less instanceof HTMLElement) less.hidden = !collapsed;
	}

	/**
	 * Anime la hauteur entre l'état replié et déplié.
	 *
	 * Une hauteur `auto` n'est pas interpolable, et le `line-clamp` continuerait
	 * de couper le texte pendant le trajet : on mesure donc les deux hauteurs
	 * réelles, on fige la hauteur de départ, puis on laisse la transition CSS
	 * rejoindre la hauteur d'arrivée avant de rendre la main au flux normal.
	 *
	 * @param {HTMLElement} description
	 * @param {boolean} opening - true = on déplie.
	 */
	#animateHeight(description, opening) {
		if (prefersReducedMotion()) return;

		const from = description.getBoundingClientRect().height;

		// Hauteur d'arrivée, mesurée dans l'état cible sans la peindre.
		const previous = description.dataset.collapsed;
		description.dataset.collapsed = opening ? 'false' : 'true';
		description.style.height = 'auto';
		const to = description.getBoundingClientRect().height;
		description.dataset.collapsed = previous;

		description.style.height = `${from}px`;
		description.style.overflow = 'hidden';

		// Force le calcul du style de départ, sinon les deux hauteurs sont
		// appliquées dans le même lot et rien ne s'anime.
		void description.offsetHeight;

		description.style.transition = 'height var(--animation-speed-slow) var(--ease-out-cubic)';
		description.style.height = `${to}px`;

		description.addEventListener(
			'transitionend',
			(event) => {
				if (event.propertyName !== 'height') return;
				description.style.removeProperty('height');
				description.style.removeProperty('overflow');
				description.style.removeProperty('transition');
			},
			{ once: true }
		);
	}
}

if (!customElements.get('ax-collection-heading')) {
	customElements.define('ax-collection-heading', AxCollectionHeading);
}
